import { format } from "date-fns";
import React from "react";

// components
import FilterChips from "../../../../../../components/FilterChips";
import { Filter } from "../../../../../../components/FilterChips/FilterChips";
import Spacer from "../../../../../../components/Spacer";
import Typography from "../../../../../../components/Text";
import { AlignmentFilters } from "./Filters";

// css
import css from "../Alignment.module.scss";

const convertFiltersToReadableList = (filters?: Partial<AlignmentFilters>) => {
	const readableList: Filter[] = [];
	if (filters) {
		Object.keys(filters).forEach((filterKey) => {
			const filter = filters?.[filterKey];

			if (filterKey === "assignee" && filter && filter.length > 0) {
				readableList.push({
					id: "assignee",
					type: "multiple-employees",
					label: "Assignee",
					value: filter.map((assignee) => ({ name: assignee.name, id: assignee.id })),
				} as Filter);
			}

			if (filterKey === "dueDateAfter") {
				readableList.push({
					id: "dueDateAfter",
					type: "string",
					label: "Due After",
					value: format(filter, "MMM d, yyyy"),
				} as Filter);
			}

			if (filterKey === "dueDateBefore") {
				readableList.push({
					id: "dueDateBefore",
					type: "string",
					label: "Due Before",
					value: format(filter, "MMM d, yyyy"),
				} as Filter);
			}

			if (filterKey === "category" && filter.length > 0) {
				readableList.push({
					id: "category",
					type: "string",
					label: "Category",
					value: filter.map((category) => category.name),
				} as Filter);
			}

			if (filterKey === "status" && filter.length > 0) {
				readableList.push({
					id: "status",
					type: "string",
					label: "Status",
					value: filter.map((status) => {
						if (status === "active") return "Active";
						if (status === "done") return "Completed";
						if (status === "archived") return "Archived";
						return status;
					}),
				} as Filter);
			}

			if (filterKey === "outlook" && filter.length > 0) {
				readableList.push({
					id: "outlook",
					type: "customRender",
					label: "Outlook",
					value: (
						<div className={css.readableOutlookChip}>
							{filter.map((outlook, index) => (
								<span
									key={`outlook-${outlook.name}-${outlook?.color}-${index}`}
									className={css.outlookEntryContainer}
									data-testid="outlook-entry"
								>
									<div className={css.outlookColor} style={{ backgroundColor: outlook.color }} />
									<span className={css.outlookName}>
										{outlook.name}
										{index < filter.length - 1 ? ", " : ""}
									</span>
								</span>
							))}
						</div>
					),
				} as Filter);
			}
		});
	}

	return readableList;
};

type ReadableGoalsFilterChipsProps = {
	filters: Partial<AlignmentFilters>;
	setFilters: (newFilters) => void;
};

const ReadableGoalsFilterChips = ({ filters, setFilters }: ReadableGoalsFilterChipsProps) => {
	const convertedFilters = convertFiltersToReadableList(filters);

	if (convertedFilters.length === 0) {
		return null;
	}

	return (
		<div className={css.filterChips}>
			<FilterChips
				filters={convertedFilters}
				label="Filters applied: "
				onDeleteFilter={(id) => {
					const currentFilters = { ...filters };
					delete currentFilters[id];

					setFilters({ ...currentFilters });
				}}
			/>
			<Spacer height={1} />
			<Typography size="paragraph-3" weight="regular" color="neutral-7">
				Showing all goals that match or have supporting goals that match filter criteria.
			</Typography>
		</div>
	);
};

export default ReadableGoalsFilterChips;
